import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight,
  ExternalLink, 
  Calendar, 
  Target, 
  TrendingUp, 
  Award,
  CheckCircle,
  Globe,
  Smartphone,
  Image as ImageIcon,
  Video,
  Code,
  BarChart3,
  Clock,
  Star,
  X
} from 'lucide-react';

const ClientDetail: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const clientData = [
    {
      name: 'Client 1',
      workImages: [
        { url: 'https://via.placeholder.com/800x600?text=Project+Work+1', title: 'Project Screenshot 1', category: 'Web Design' },
        { url: 'https://via.placeholder.com/800x600?text=Project+Work+2', title: 'Project Screenshot 2', category: 'Web Design' },
      ],
    },
    {
      name: 'Client 2',
      workImages: [
        { url: 'https://via.placeholder.com/800x600?text=Project+Work+3', title: 'Project Screenshot 3', category: 'Web Design' },
        { url: 'https://via.placeholder.com/800x600?text=Project+Work+4', title: 'Project Screenshot 4', category: 'Web Design' },
      ],
    },
  ];

  // Flatten all workImages from all clients into a single array
  const allWorkImages = clientData.flatMap((client: any) => client.workImages);


  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-primary-50 via-accent-50 to-primary-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent-500 rounded-full blur-3xl"></div>
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <Link
              to="/clients"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium mb-8 transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Clients
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Infinite Loop Carousel - Images Only */}
      <section className="py-16 bg-gradient-to-r from-primary-50 to-accent-50 overflow-hidden">
        <div className="container-custom mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              Our Work Showcase
            </h2>
            <p className="text-neutral-600">
              Scroll through our creative portfolio
            </p>
          </motion.div>
        </div>
        <div className="relative">
          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-primary-50 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-accent-50 to-transparent z-10 pointer-events-none"></div>
          <div className="flex gap-6 infinite-scroll-container" style={{ width: 'fit-content', animationDuration: '30s' }}>
            {/* Duplicate items for seamless loop - Show only images */}
            {[...Array(3)].map((_, loopIndex) => (
              <React.Fragment key={loopIndex}>
                {allWorkImages.map((work: any, index: number) => (
                  <div
                    key={`${loopIndex}-${index}`}
                    className="flex-shrink-0 w-80 h-64 bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                    onClick={() => setSelectedImage(index)}
                  >
                    <div className="w-full h-full relative">
                      <img
                        src={work.url}
                        alt={work.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'https://via.placeholder.com/800x600?text=Work+Image';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                        <div className="text-white">
                          {work.category && (
                            <div className="text-xs font-medium mb-1 opacity-90">{work.category}</div>
                          )}
                          <h4 className="text-lg font-bold">{work.title}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>


      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-primary-400 transition-colors duration-300 z-10"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              src={allWorkImages[selectedImage]?.url}
              alt={allWorkImages[selectedImage]?.title}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary-500 to-accent-500">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
          >
            <h2 className="text-4xl font-serif font-bold text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's create something extraordinary for your brand. Get in touch with us today.
            </p>
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-accent-50 transition-colors duration-300 inline-flex items-center shadow-lg"
            >
              Start Your Project
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ClientDetail;

